import * as React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import PlayCircleFilledWhiteIcon from '@mui/icons-material/PlayCircleFilledWhite';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import AutoFixHighRoundedIcon from '@mui/icons-material/AutoFixHighRounded';
import ConstructionRoundedIcon from '@mui/icons-material/ConstructionRounded';
import QueryStatsRoundedIcon from '@mui/icons-material/QueryStatsRounded';
import SettingsSuggestRoundedIcon from '@mui/icons-material/SettingsSuggestRounded';
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded';
import ThumbUpAltRoundedIcon from '@mui/icons-material/ThumbUpAltRounded';
import Reveal from './Reveal';
import { BRAND } from '../theme/landingTheme';

const VIDEOS = {
  extension: {
    id: 'SG-GYBsfGZc',
    label: 'Chrome Extension',
    title: 'Marter Chrome extension demo video',
  },
  mobile: {
    id: 'n-j15j38HRE',
    label: 'Mobile App',
    title: 'Marter mobile app demo video',
  },
};

const items = [
  {
    icon: <QueryStatsRoundedIcon />,
    title: 'Data you can act on',
    description:
      'Sales and price patterns for any Walmart product, so every sourcing decision is backed by history — not guesswork.',
  },
  {
    icon: <ConstructionRoundedIcon />,
    title: 'Set up in minutes',
    description:
      'Install the Chrome extension and see live data right on Walmart product pages. Mobile and desktop apps included.',
  },
  {
    icon: <ThumbUpAltRoundedIcon />,
    title: 'Built for your routine',
    description:
      'An intuitive interface that fits into your daily sourcing flow instead of adding another tab to fight with.',
  },
  {
    icon: <AutoFixHighRoundedIcon />,
    title: 'Always improving',
    description:
      'New features ship regularly, built around what Walmart sellers actually ask for.',
  },
  {
    icon: <SupportAgentRoundedIcon />,
    title: 'Real support',
    description:
      'Responsive humans at info@getmarter.com — before and after you subscribe.',
  },
  {
    icon: <SettingsSuggestRoundedIcon />,
    title: 'Your feature requests, built',
    description:
      'Missing something? Email us and we will add it — subscriber suggestions drive the roadmap.',
  },
];

// Lazy YouTube embed: shows the thumbnail + play button, only loads the
// iframe after a click. Keeps ~500KB of YouTube JS off the initial load.
function LazyVideo({ videoId, title, playing, setPlaying }) {
  if (playing) {
    return (
      <Box sx={{ position: 'relative', width: '100%', pt: '56.25%', borderRadius: '14px', overflow: 'hidden' }}>
        <iframe
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
          src={`https://www.youtube.com/embed/${videoId}?autoplay=1`}
          title={title}
          allow="fullscreen; accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      </Box>
    );
  }

  return (
    <Box
      onClick={() => setPlaying(true)}
      role="button"
      aria-label={`Play: ${title}`}
      sx={{
        position: 'relative',
        width: '100%',
        pt: '56.25%',
        borderRadius: '14px',
        overflow: 'hidden',
        cursor: 'pointer',
        '&:hover .lp-play-icon': { transform: 'translate(-50%, -50%) scale(1.1)' },
      }}
    >
      <Box
        component="img"
        src={`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`}
        alt={`${title} preview`}
        sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
      />
      <PlayCircleFilledWhiteIcon
        className="lp-play-icon"
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          fontSize: '84px',
          color: BRAND.yellow,
          transition: 'transform 0.2s ease',
          filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.5))',
        }}
      />
    </Box>
  );
}

export default function Highlights() {
  const [selected, setSelected] = React.useState('extension');
  const [playing, setPlaying] = React.useState(false);

  const handleSelect = (event, value) => {
    if (value !== null && value !== selected) {
      setSelected(value);
      setPlaying(false); // stop the previous video when switching
    }
  };

  return (
    <Box
      id="demo"
      className="lp-dark-bg"
      sx={{
        pt: { xs: 8, sm: 12 },
        pb: { xs: 8, sm: 16 },
        color: 'white',
      }}
    >
      <Container
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: { xs: 3, sm: 6 },
        }}
      >
        <Box sx={{ width: { sm: '100%', md: '70%' }, textAlign: 'center' }}>
          <Typography component="h2" variant="h4" sx={{ color: '#fff' }}>
            See Marter in action
          </Typography>
          <Typography variant="body1" sx={{ color: 'grey.400', mt: 1.5 }}>
            Two minutes from install to your first product analysis — watch how sellers
            use price history, sales estimates and alerts to pick winners.
          </Typography>
        </Box>

        <ToggleButtonGroup
          value={selected}
          exclusive
          onChange={handleSelect}
          aria-label="choose demo video"
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.06)',
            borderRadius: '12px',
            p: 0.5,
            '& .MuiToggleButton-root': {
              border: 'none',
              borderRadius: '10px !important',
              px: { xs: 2, sm: 3 },
              fontWeight: 700,
              textTransform: 'none',
              color: 'grey.400',
              fontSize: { xs: '0.85rem', sm: '0.9rem' },
            },
            '& .Mui-selected': {
              backgroundColor: `${BRAND.yellow} !important`,
              color: `${BRAND.ink} !important`,
            },
          }}
        >
          {Object.entries(VIDEOS).map(([key, video]) => (
            <ToggleButton key={key} value={key} aria-label={`${video.label} demo`}>
              {video.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <Reveal style={{ width: '100%', maxWidth: '860px' }}>
          <LazyVideo
            videoId={VIDEOS[selected].id}
            title={VIDEOS[selected].title}
            playing={playing}
            setPlaying={setPlaying}
          />
        </Reveal>

        <Grid container spacing={2.5} sx={{ mt: { xs: 1, sm: 2 } }}>
          {items.map((item, index) => (
            <Grid item xs={12} sm={6} md={4} key={item.title}>
              <Reveal delay={index % 3} style={{ height: '100%' }}>
                <Card
                  sx={{
                    p: 3,
                    height: '100%',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 202, 24, 0.15)',
                    color: '#fff',
                    boxShadow: 'none',
                  }}
                >
                  <Box sx={{ color: BRAND.yellow, mb: 1.5 }}>{item.icon}</Box>
                  <Typography variant="h6" sx={{ fontSize: '1.05rem', mb: 0.75 }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'grey.400' }}>
                    {item.description}
                  </Typography>
                </Card>
              </Reveal>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}
